// Sandboxed markdown renderer for USER-AUTHORED comment bodies (TPT359).
//
// renderMarkdown() (utils.js) is a bare marked.parse() on the global marked singleton:
// raw HTML passes through verbatim and link/image hrefs are only encodeURI()'d, so a
// comment could carry <script>, <img onerror=...> or a javascript:/data: link into
// innerHTML. This module renders the same GFM subset through a DEDICATED Marked
// instance whose renderer neutralises both holes. The global `marked` is never touched,
// so descriptions and the create-task preview keep their existing behaviour.
//
// Leaf module on purpose: imports only `marked` (no utils.js/api.js, which pull in the
// DOM), so it is node-testable with a plain static import — same as mention-highlight.js.
//
// Rules:
//   - raw HTML (block or inline) is emitted as escaped, inert text; a pure HTML comment
//     is dropped so `<!-- trello-action:x -->` markers stay invisible.
//   - link/image URLs: only http(s) and scheme-less (relative) URLs survive; anything
//     else degrades to its plain text (link) / alt text (image).
//   - every attribute value is fully escaped, `&` included — marked leaves it raw, which
//     lets `&#106;avascript:` decode to `javascript:` in the browser.
//   - absolute http(s) links open in a new tab; relative links (/files/ downloads, #/
//     hash routes) stay in the SPA tab.
import { Marked } from 'marked';

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

// Same five-character map as marked's own escape() and mention-highlight.js's
// escapeLikeMarked(), so escaped handles inside our output still match there.
function escapeHtmlText(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (ch) => ESCAPES[ch]);
}

const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;
const HTTP_RE = /^https?:/i;
const HTML_COMMENT_RE = /^\s*<!--[\s\S]*?-->\s*$/;

// isSafeMarkdownUrl(href) — returns the cleaned URL to emit, or null when it must be
// rejected. encodeURI() is marked's own normalisation step; as a side effect it
// percent-encodes whitespace and control characters, so `java\tscript:` becomes
// `java%09script:` — no longer a scheme (`%` is not a scheme character), i.e. an inert
// relative URL. An empty href is rejected so `[x]()` renders as plain text.
export function isSafeMarkdownUrl(href) {
  if (typeof href !== 'string') return null;
  let clean;
  try {
    clean = encodeURI(href).replace(/%25/g, '%');
  } catch {
    return null;
  }
  if (!clean) return null;
  if (SCHEME_RE.test(clean)) return HTTP_RE.test(clean) ? clean : null;
  return clean;
}

const instance = new Marked({
  gfm: true,
  breaks: true,
  renderer: {
    html({ text, block }) {
      if (HTML_COMMENT_RE.test(text)) return '';
      if (block) {
        return `<p>${escapeHtmlText(text.replace(/\s+$/, '')).replace(/\n/g, '<br>')}</p>\n`;
      }
      return escapeHtmlText(text);
    },
    link({ href, title, tokens }) {
      const inner = this.parser.parseInline(tokens);
      const clean = isSafeMarkdownUrl(href);
      if (clean === null) return inner;
      let out = `<a href="${escapeHtmlText(clean)}"`;
      if (title) out += ` title="${escapeHtmlText(title)}"`;
      if (HTTP_RE.test(clean)) out += ' target="_blank" rel="noopener noreferrer"';
      return `${out}>${inner}</a>`;
    },
    image({ href, title, text, tokens }) {
      const alt = tokens ? this.parser.parseInline(tokens, this.parser.textRenderer) : text;
      const clean = isSafeMarkdownUrl(href);
      if (clean === null) return escapeHtmlText(alt);
      let out = `<img src="${escapeHtmlText(clean)}" alt="${escapeHtmlText(alt)}"`;
      if (title) out += ` title="${escapeHtmlText(title)}"`;
      return `${out} loading="lazy">`;
    },
  },
});

export function renderSafeMarkdown(text) {
  if (!text) return '';
  return instance.parse(String(text));
}
